import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {ProTasksDoneEntry} from './done/model/pro-tasks-done.entry';
import {ProTasksEditEntry} from './form/model/pro-tasks-edit.entry';
import {InfoOptionEntry} from '../tasks-manager/tasks-queue/model/info-option.entry';
import {
  VehicleParametersForm
} from '../../vehicles/vehicles-list/vehicle-info/vehicle-parameters/model/vehicle-parameters.component';
import {DirectionsRequest} from '../../../../core/direction/directions-request';
import {MapPosition} from '../route/metamodel';
import {HttpUtils} from '../../../../core/page/commons/http.utils';
import {TaskUtils} from '../tasks-manager/utils/task-utils';
import {UntypedFormGroup} from '@angular/forms';
import {ProTasksRouteRequest} from './form/model/pro-tasks-route-request';
import {TemplateRequest} from './form/properties/templates/model/template-request';
import {POIStationEntry} from './form/model/poi-station.entry';
import {ProTasksPreviewEntry} from './queue/model/pro-tasks-preview.entry';
import {ProTasksQueueResponse} from './queue/model/pro-tasks-queue.response';
import {ProTasksRouteResponse} from './form/model/pro-tasks-route-response';
import {POIParkingStationEntry} from './form/model/poi-parking-details.entry';
import {IntersectingMarkedRoadEntry} from './form/model/intersecting-marked-road.entry';
import {TemplatesEntry} from './form/properties/templates/model/template.entry';
import {CabotageTasksRequest} from './form/model/cabotage-tasks-request';
import {ProTasksDeletedEntry} from './deleted/model/pro-tasks-deleted.entry';
import {ProTasksFormData} from './form/model/pro-tasks-form.data';
import {LkwOrderEntry} from './lkw-orders/model/lkw-order.entry';
import {UnveroRequest} from './form/model/unvero/unvero-request';
import {TaskEtaRequest} from './form/model/task-eta-request';
import {UnveroSelectedStationEntry} from './form/model/unvero/unvero-selected-station.entry';
import {UnveroFuelStationsResponseEntry} from './form/model/unvero/unvero-fuel-stations-response.entry';
import {PreferableRouteDetectionRequest} from '../../route/preferable-route/model/preferable-route-detection.request';
import {FuelInfo} from './form/properties/fuel-chart/fuel-calculation.utils';
import {TaskFuelInfo} from './form/model/TaskFuelInfo';
import {TaskEtaData} from "./form/properties/parkings/model/task-eta-data";
import {ProTaskForm} from './form/model/pro-task.form';
import {ETARestingsResponse} from "./form/properties/parkings/model/eta-restings-response";

@Injectable()
export class ProTasksManagerService {

  constructor(private http: HttpClient) {
  }

  public queue(vehicle: number, skipLoader?: boolean): Observable<ProTasksQueueResponse> {
    let headers = skipLoader ? {headers: {skip: 'true'}} : {};
    return this.http.get<ProTasksQueueResponse>(`/api/pro-tasks/${vehicle}/queue`, headers);
  }

  public done(vehicle: number, page: number, useOffset: boolean): Observable<ProTasksDoneEntry[]> {
    return this.http.get<ProTasksDoneEntry[]>(`/api/pro-tasks/${vehicle}/done?page=${page}&useOffset=${useOffset}`, {});
  }

  public deleted(vehicle: number, page: number, useOffset: boolean): Observable<ProTasksDeletedEntry[]> {
    return this.http.get<ProTasksDeletedEntry[]>(`/api/pro-tasks/${vehicle}/deleted?page=${page}&useOffset=${useOffset}`);
  }

  public lkwOrders(page: number, withoutTasks: boolean): Observable<LkwOrderEntry[]> {
    let params = new HttpParams();
    params = HttpUtils.appendParam(params, 'page', page);
    params = HttpUtils.appendParam(params, 'withoutTasks', withoutTasks);
    return this.http.get<LkwOrderEntry[]>(`/api/orders/lkw-walter`, {params});
  }

  public load(vehicle: number): Observable<ProTasksEditEntry> {
    return this.http.get<ProTasksEditEntry>(`/api/pro-tasks/${vehicle}/load`);
  }

  public loadFuelInfo(vehicle: number): Observable<TaskFuelInfo[]> {
    return this.http.get<TaskFuelInfo[]>(`/api/pro-tasks/${vehicle}/load-fuel-info`);
  }

  public loadReset(vehicle: number, reset: number, fuelLevel: number): Observable<ProTasksEditEntry> {
    return this.http.get<ProTasksEditEntry>(`/api/pro-tasks/${vehicle}/load/reset?taskId=${reset}&fuelLevel=${fuelLevel}`);
  }

  public save(vehicle: number,
              forms: UntypedFormGroup[],
              confirmTasks: boolean,
              fuelWarning: boolean,
              enabledDefaultWaypoints: boolean,
              copyEnabled: boolean,
              driverLang,
              lkwOrderId: number,
              unveroSelectedStation: UnveroSelectedStationEntry): Observable<Response> {

    const data = new ProTasksFormData(
      forms.map(it => it.value),
      confirmTasks,
      fuelWarning,
      enabledDefaultWaypoints,
      copyEnabled,
      driverLang,
      lkwOrderId,
      unveroSelectedStation);

    return this.http.post<Response>(`/api/pro-tasks/${vehicle}/save`, data);
  }

  public saveFile(uuid: string, file: any, vehicle: number): Observable<any> {
    let formData: FormData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post(`/api/pro-tasks/${vehicle}/file?uuid=${uuid}`, formData, {
      headers: new HttpHeaders()
    });
  }

  public options(): Observable<InfoOptionEntry[]> {
    return this.http.get<InfoOptionEntry[]>(`/api/tasks-options`);
  }

  public vehicleParams(vehicle: number) {
    return this.http.get<VehicleParametersForm>(`/api/vehicle-params?vehicleId=${vehicle}`);
  }

  public googleDirections(vehicleId: number, request: DirectionsRequest, waypoints: MapPosition[]): Observable<string> {
    let params = new HttpParams();
    params = HttpUtils.appendParam(params, 'origin.lat', `${request.origin.lat}`);
    params = HttpUtils.appendParam(params, 'origin.lng', `${request.origin.lng}`);
    params = HttpUtils.appendParam(params, 'destination.lat', `${request.destination.lat}`);
    params = HttpUtils.appendParam(params, 'destination.lng', `${request.destination.lng}`);

    waypoints.forEach((it, idx) => {
      let waypoint = TaskUtils.toMapPosition(it);
      params = HttpUtils.appendParam(params, `waypoints[${idx}].lat`, waypoint.lat);
      params = HttpUtils.appendParam(params, `waypoints[${idx}].lng`, waypoint.lng);
    });
    params = HttpUtils.appendParam(params, 'avoids', `${request.avoids}`);

    return this.http.get<string>(`/api/tasks/${vehicleId}/google-directions`, {params: params});
  }

  public confirmTask(id: number, vehicleId: number, copyEnabled: boolean): Observable<Response> {
    return this.http.post<Response>(`/api/pro-tasks/${vehicleId}/confirm?task=${id}&copyEnabled=${copyEnabled}`, {});
  }

  public confirmAll(id: number, copyEnabled: boolean): Observable<Response> {
    return this.http.post<Response>(`/api/pro-tasks/${id}/confirm-all?copyEnabled=${copyEnabled}`, {});
  }

  public markAsExecuting(id: number, vehicleId: number): Observable<any> {
    return this.http.post(`/api/pro-tasks/${vehicleId}/mark-as-executing?task=${id}`, {});
  }

  public markAsDone(id: number, vehicleId: number): Observable<Response> {
    return this.http.post<Response>(`/api/pro-tasks/${vehicleId}/mark-as-done?task=${id}`, {});
  }

  public rollbackToExecuting(id: number, vehicleId: number): Observable<void> {
    return this.http.post<void>(`/api/pro-tasks/${vehicleId}/rollback-to-executing?task=${id}`, {});
  }

  public findTruckRoute(request: ProTasksRouteRequest,
                        vehicleId: number,
                        alternatives: boolean,
                        zoom: number): Observable<ProTasksRouteResponse[]> {

    return this.http.post<ProTasksRouteResponse[]>(
      `/api/routing/truck-route?alternatives=${alternatives}&mapZoom=${zoom}`,
      request);
  }

  public deleteAll(id: number): Observable<Response> {
    // TODO addapt
    return this.http.delete<Response>(`/api/tasks/${id}/delete-all`);
  }

  public resetStatus(id: number, vehicle: number): Observable<Response> {
    return this.http.post<Response>(`/api/pro-tasks/${vehicle}/reset?task=${id}`, {});
  }

  public path(vehicle: number, id: number): Observable<string> {
    return this.http.get(`/api/pro-tasks/${vehicle}/${id}/path`, {responseType: 'text'});
  }

  public driverLanguage(vehicleId: number): Observable<any> {
    return this.http.get(`/api/tasks/${vehicleId}/driver-language`, {responseType: 'text'});
  }

  public translationFile(lang: string): Observable<any> {
    return this.http.get(`assets/i18n/${lang.toLowerCase()}.json`, {headers: {skip: 'true'}});
  }

  // ----------------------- Templates --------------------

  public fetchTemplates(): Observable<TemplatesEntry[]> {
    return this.http.get<TemplatesEntry[]>('/api/templates');
  }

  public deleteTemplate(id: number) {
    return this.http.delete(`/api/templates?id=${id}`);
  }

  public saveTemplate(templateRequest: TemplateRequest): Observable<Response> {
    return this.http.post<Response>(`/api/templates`, templateRequest);
  }

  // -------------------------------------------------------

  public getUnveroFuelStations(vehicle: number, request: UnveroRequest): Observable<UnveroFuelStationsResponseEntry> {
    return this.http.post<UnveroFuelStationsResponseEntry>(`/api/pro-tasks/${vehicle}/unvero-fuel-stations`, request);
  }

  // public getFuelStationsNearRoute(vehicle: number, todo
  //                                 routeDistance: number,
  //                                 polylines: string[],
  //                                 language: string): Observable<POIStationEntry[]> {
  //
  //   let params = new HttpParams();
  //   params = HttpUtils.appendParam(params, 'routeDistance', routeDistance);
  //   params = HttpUtils.appendParam(params, 'language', language);
  //   return this.http.post<POIStationEntry[]>(
  //     `/api/pro-tasks/${vehicle}/fuel-stations-near-route`,
  //     polylines,
  //     {headers: {skip: 'true'}, params: params}
  //   );
  // }

  public getParkingStationsNearRoute(vehicle: number,
                                     routeDistance: number,
                                     polylines: string[],
                                     language: string): Observable<POIParkingStationEntry[]> {

    return this.http.post<POIParkingStationEntry[]>(
      `/api/pro-tasks/${vehicle}/parking-stations-near-route?routeDistance=${routeDistance}&language=${language}`,
      polylines,
      {headers: {skip: 'true'}}
    );
  }

  public findIntersectingMarkedRoads(vehicleId: number, roadPolyline: string): Observable<IntersectingMarkedRoadEntry[]> {
    return this.http.post<IntersectingMarkedRoadEntry[]>(
      `/api/pro-tasks/${vehicleId}/intersecting-marked-roads`,
      roadPolyline,
      {headers: {skip: 'true'}}
    );
  }

  public getMarkedPaidRoadsTolls(vehicleId: number, roadPolyline: string): Observable<number> {
    return this.http.post<number>(
      `/api/pro-tasks/${vehicleId}/marked-paid-roads-tolls`,
      roadPolyline,
      {headers: {skip: 'true'}}
    );
  }

  public findFavoritePoisNearRoute(vehicle: number,
                                   routeDistance: number,
                                   polylines: string[],
                                   language: string): Observable<POIStationEntry[]> {

    return this.http.post<POIStationEntry[]>(
      `/api/pro-tasks/${vehicle}/favorites-near-route?routeDistance=${routeDistance}&language=${language}`,
      polylines,
      {headers: {skip: 'true'}}
    );
  }

  public fetchTasksPreview(vehicle: number): Observable<ProTasksPreviewEntry[]> {
    return this.http.get<ProTasksPreviewEntry[]>(`/api/pro-tasks/${vehicle}/fetch-tasks-preview`);
  }

  public fetchFuelInfo(vehicle: number): Observable<FuelInfo> {
    return this.http.get<FuelInfo>(`/api/pro-tasks/${vehicle}/fuel-info`);
  }

  public getRestingsETA(vehicle: number, deviceKey: string, taskData: TaskEtaData[]): Observable<ETARestingsResponse> {
    return this.http.post<ETARestingsResponse>(`/api/pro-tasks/${vehicle}/resting-eta?deviceKey=${deviceKey}`, taskData);
  }

  public getETA(vehicle: number, request: TaskEtaRequest[]): Observable<string> {
    return this.http.post(`/api/pro-tasks/${vehicle}/eta`, request, {responseType: 'text'});
  }

  public getETAQueue(vehicle: number, request: TaskEtaRequest[]): Observable<string[]> {
    return this.http.post<string[]>(`/api/pro-tasks/${vehicle}/eta/queue`, request);
  }

  public assignOrderId(vehicleId: number, orderId: number, taskIds: number[]): Observable<void> {
    return this.http.post<void>(`/api/tasks/${vehicleId}/assign-order?orderId=${orderId}`, taskIds);
  }

  public getActualPolyline(vehicle: number, startedAt: string, skipLoader?: boolean): Observable<string> {
    let skipLoaderHeader = skipLoader ? {skip: 'true'} : {};
    return this.http.get(`/api/pro-tasks/${vehicle}/actual-route?startedAt=${startedAt}`, {
      responseType: 'text',
      headers: skipLoaderHeader
    });
  }

  public detectCabotageViolation(vehicle: number, request: CabotageTasksRequest[]): Observable<boolean> {
    return this.http.post<boolean>(`/api/pro-tasks/${vehicle}/detect-cabotage-violation`, request);
  }

  public updateEmptyDistance(vehicle: number, taskId: number, emptyDistance: boolean): Observable<void> {
    return this.http.post<void>(`/api/pro-tasks/${vehicle}/update-empty-distance?taskId=${taskId}&emptyDistance=${emptyDistance}`, {});
  }

  public saveExternalOrder(request: any): Observable<void> {
    return this.http.post<void>(`/api/pro-tasks/${request.vehicleId}/save-external-order`, request);
  }

  public haveTasksChanged(vehicle: number, previousTasks: number[], lastKnownModification: string): Observable<boolean> {
    let lastModification = lastKnownModification != null ? lastKnownModification : '';
    return this.http.post<boolean>(
      `/api/tasks/${vehicle}/tasks-changed?lastKnownModification=` + lastModification, previousTasks
    );
  }

  public intersection(route: PreferableRouteDetectionRequest): Observable<ProTasksRouteResponse[]> {
    return this.http.post<ProTasksRouteResponse[]>('/api/preferable-route/find', route)
  }

  public suggestPlan(vehicleId: number, queue: ProTaskForm[], init: boolean, suggestionId: number): Observable<any> {
    let params = new HttpParams();
    params = HttpUtils.appendParam(params, 'vehicleId', vehicleId);
    params = HttpUtils.appendParam(params, 'init', init);
    if (suggestionId) {
      params = HttpUtils.appendParam(params, 'suggestionId', suggestionId);
    }
    return this.http.post(`/api/optimal-fuelling/suggest-plan`, queue, {params: params});
  }

  public checkTrafficBans(forms: ProTaskForm[], vehicleId: number): Observable<ProTaskForm[]> {
    return this.http.post<ProTaskForm[]>(`/api/traffic-ban/check-bans?vehicleId=${vehicleId}`, forms);
  }
}
